import {Fragment, useCallback, useMemo, useRef, useState} from 'react'
import {BreakpointSize, Button, Grid, Image, Link, Text, useWindowSize} from '@primer/react-brand'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'
import {Spacer} from './_shared'
import SectionIntro from './components/SectionIntro/SectionIntro'
import {COPY, CUSTOMER_STORIES, StoryCategory} from './CustomerStories.data'
import Toggle from './components/Toggle/Toggle'
import {wait} from './utils/time'

export default function CustomerStories() {
  const [currentCategory, setCurrentCategory] = useState<StoryCategory>(StoryCategory.Industry)
  const gridWrapperRef = useRef<HTMLDivElement>(null)
  const windowSize = useWindowSize()

  const currentCategoryIndex = useMemo(() => Number(currentCategory), [currentCategory])

  const currentStories = useMemo(
    () =>
      Object.values(CUSTOMER_STORIES[currentCategory])
        .map(subCategories => {
          return Object.values(subCategories).sort((a, b) => Number(a.subCategory) - Number(b.subCategory))
        })
        .flat(),
    [currentCategory],
  )

  const onCategoryChange = useCallback(
    async (category: StoryCategory) => {
      if (!gridWrapperRef.current || category === currentCategory) return

      const grid = gridWrapperRef.current.querySelector('.lp-CustomerStories-grid')
      if (!grid) return

      grid.classList.add('lp-CustomerStories-grid--hidden')
      await wait(200)

      setCurrentCategory(category)
      await wait(200)

      grid.classList.remove('lp-CustomerStories-grid--hidden')
    },
    [currentCategory],
  )

  return (
    <section id="customer-stories" className="lp-Section">
      <div className="lp-CustomerStories-stickyContainer">
        <div className="lp-SectionBlock lp-SectionBlock--noBorder">
          <div className="lp-SectionBlock-content">
            <SectionIntro mascot="ducky" title={COPY.intro.title} bottomSpacerSize="small" isWebGLMascotOnly />
          </div>
        </div>

        <div className="lp-CustomerStories-toggleWrapper">
          <Toggle
            currentIndex={currentCategoryIndex}
            items={Object.entries(COPY.content.categories).map(([category, label]) => ({label, value: category}))}
            ariaLabel={COPY.content.aria.toggle}
            onToggle={(value: string) => onCategoryChange(Number(value) as StoryCategory)}
            hasBackground
          />
        </div>

        <Spacer size="32px" size1012="56px" />

        <hr className="lp-CustomerStories-sectionBlockDivider" />

        <div ref={gridWrapperRef} className="lp-SectionBlock">
          <div className="lp-SectionBlock-content lp-SectionBlock-content--lines lp-SectionBlock-content--narrow">
            <Grid className="lp-CustomerStories-grid lp-CustomerStories-grid--customerStories">
              {currentStories.map((story, index) => (
                <Grid.Column
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={`story_${index}`}
                  className="lp-CustomerStories-gridColumn"
                  span={{xsmall: 12, medium: 12, large: 4}}
                  style={{['--backgroundUrl']: `url(${story.backgroundUrl})`} as React.CSSProperties}
                >
                  {/* TODO: See if we can remove the `nested a tags` warning */}
                  <Button
                    className="lp-CustomerStories-button"
                    as="a"
                    href={story.url}
                    hasArrow={false}
                    {...getAnalyticsEvent({
                      action: COPY.content.readMore,
                      tag: 'link',
                      context: COPY.content.subCategories[story.subCategory],
                      location: COPY.analyticsId,
                    })}
                  >
                    <div className="lp-CustomerStories-logoContainer">
                      <Image
                        className="lp-CustomerStories-logo"
                        src={story.logo.url}
                        alt={story.logo.alt}
                        style={{height: story.logo.height}}
                        loading="lazy"
                      />
                    </div>

                    <div>
                      <Text className="lp-CustomerStories-label" variant="muted" weight="semibold">
                        {COPY.content.subCategories[story.subCategory]}
                      </Text>
                      <Text className="lp-CustomerStories-description" as="p">
                        {story.description}
                      </Text>

                      <div className="lp-CustomerStories-storyLink">
                        <Link variant="accent">
                          <Text weight="normal">{COPY.content.readMore}</Text>
                        </Link>
                      </div>
                    </div>
                  </Button>
                </Grid.Column>
              ))}

              {/* Add empty column to fill the grid if needed */}
              {[BreakpointSize.LARGE, BreakpointSize.XLARGE, BreakpointSize.XXLARGE].includes(
                windowSize.currentBreakpointSize!,
              ) &&
                currentStories.length % 3 !== 0 && (
                  <Grid.Column
                    className="lp-CustomerStories-gridColumn lp-CustomerStories-gridColumn--dummy"
                    span={{xsmall: 12, medium: 6, large: 4}}
                  />
                )}
            </Grid>
          </div>
        </div>
      </div>

      <div className="lp-SectionBlock lp-SectionBlock--noBorder">
        <div className="lp-SectionBlock-content">
          <Spacer size="0px" size1012="48px" />

          {/* Links */}
          <div className="lp-CustomerStories-links">
            {COPY.links.map((link, index) => (
              // eslint-disable-next-line @eslint-react/no-array-index-key
              <Fragment key={`link_${index}`}>
                <div>
                  <Link
                    variant="accent"
                    className="lp-CustomerStories-link"
                    href={link.url}
                    {...getAnalyticsEvent({
                      action: link.label,
                      tag: 'link',
                      context: 'footer',
                      location: COPY.analyticsId,
                    })}
                  >
                    <Text weight="normal" size="300">
                      {link.label}
                    </Text>
                  </Link>
                </div>

                {index < COPY.links.length - 1 && <hr className="lp-CustomerStories-divider" />}
              </Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

try{ CustomerStories.displayName ||= 'CustomerStories' } catch {}